import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  FilePdf,
  FileXls,
  Printer,
  PaperPlaneTilt,
  ShieldCheck,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { printPdf as printPdfUtil, downloadPdf as downloadPdfUtilFn } from '../utils/pdfUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import SendForApprovalDialog from '../components/SendForApprovalDialog';
import ApprovalStatusPanel from '../components/ApprovalStatusPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - i);

const formatDateDMY = (d) => {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

const getCatLabel = (cat, t) => {
  if (!cat || cat === 'uncategorized') return t('uncategorized');
  const key = `cat_${cat}`;
  const label = t(key);
  return label !== key ? label : cat;
};

export default function AnnualStatement() {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const printRef = useRef(null);

  const [year, setYear] = useState(String(currentYear));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);
  // Phase 707 — Send for Approval workflow
  const [approvalOpen, setApprovalOpen] = useState(false);

  const fetchStatement = async (selectedYear) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/annual-statement?year=${selectedYear}`);
      setData(res.data);
      setGenerated(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    fetchStatement(year);
  };

  const handleExportPDF = () => {
    downloadPdfUtilFn(`/buildings/${buildingId}/annual-statement/pdf`, { year, lang: language });
    toast.success(t('exportSuccess'));
  };

  const handlePrintPDF = () => {
    printPdfUtil(`/buildings/${buildingId}/annual-statement/pdf`, { year, lang: language });
  };

  const handleExportExcel = () => {
    if (!data) return;
    const rows = [];
    // Header rows
    rows.push([t('annualFinancialStatement')]);
    rows.push([`${t('buildingName')}: ${data.building.name}`]);
    rows.push([`${t('address')}: ${data.building.location}`]);
    rows.push([`${t('period')}: ${data.period_start} - ${data.period_end}`]);
    rows.push([]);
    // Phase 326 — No separate "Opening Balance" row. Both Cash OB & Bank
    // OB now live inside the ledger as normal rows (date in column A,
    // amount in Income column, etc.).
    rows.push([t('date'), t('reference'), t('description'), `${t('income')} (€)`, `${t('expense')} (€)`, `${t('balance')} (€)`]);
    // Data — preserve OB rows verbatim so Excel matches screen + PDF.
    let closingBlankInserted = false;
    data.entries.forEach(e => {
      const isOB = !!e.is_opening_balance;
      const isCB = !!e.is_closing_balance;
      // Phase 330 — Insert one blank row immediately before the first
      // closing row so closing balances visually separate from normal
      // transactions (same treatment as UI + PDF).
      if (isCB && !closingBlankInserted) {
        rows.push(['', '', '', '', '', '']);
        closingBlankInserted = true;
      }
      // For OB/CB rows the backend description already reads
      // "Cash/Bank Opening Balance" / "Cash/Bank Closing Balance";
      // render it as-is. For categorized rows prefix with the i18n cat
      // label so Excel readers see e.g. "201 Management MIC Pieri ...".
      let desc = e.description || '';
      if (!isOB && !isCB && e.category) {
        const tag = getCatLabel(e.category, t);
        const body = desc.startsWith(`${e.category} `) ? desc.slice(e.category.length + 1) : desc;
        desc = `${tag} ${body}`.trim();
      }
      // Phase 328 — Closing rows have blank Income/Expense; the balance
      // column carries the per-source closing snapshot.
      const income = isCB ? '' : (e.income > 0 ? e.income.toFixed(2) : '');
      const expense = isCB ? '' : (e.expense > 0 ? e.expense.toFixed(2) : '');
      rows.push([
        formatDateDMY(e.date),
        e.reference || '—',
        desc,
        income,
        expense,
        e.balance.toFixed(2),
      ]);
    });
    rows.push([]);
    rows.push([t('totalIncome'), '', '', data.summary.total_income.toFixed(2), '', '']);
    rows.push([t('totalExpenses'), '', '', '', data.summary.total_expenses.toFixed(2), '']);
    rows.push([`${t('finalBalance')} (31/12)`, '', '', '', '', data.summary.final_balance.toFixed(2)]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 50 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Statement ${year}`);
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const bName = data?.building?.name?.replace(/\s+/g, '_') || 'building';
    saveAs(blob, `annual_statement_${bName}_${year}.xlsx`);
    toast.success(t('exportSuccess'));
  };

  const handleSendEmail = () => {
    toast.info(t('emailFeatureComingSoon'));
  };

  return (
    <Layout>
      <div data-testid="annual-statement-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate(`/buildings/${buildingId}`)} className="rounded-none" data-testid="back-to-building-btn">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {t('annualFinancialStatement')}
            </h1>
            {data && <p className="text-sm text-zinc-500">{data.building.name} — {data.building.location}</p>}
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-4 mb-6 bg-white border border-zinc-200 p-5" data-testid="statement-controls">
          <div className="w-40">
            <label className="text-sm font-medium text-zinc-700 mb-1 block">{t('year')}</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="rounded-none" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleGenerate} className="bg-zinc-950 rounded-none" disabled={loading} data-testid="generate-report-btn">
            {loading ? t('loading') : t('generateReport')}
          </Button>
          {generated && data && (
            <div className="flex gap-2 ml-auto">
              <Button onClick={handlePrintPDF} variant="outline" className="rounded-none" data-testid="print-pdf-btn">
                <Printer size={18} className="mr-2 text-blue-600" />{t('printPdf')}
              </Button>
              <Button onClick={handleExportPDF} variant="outline" className="rounded-none" data-testid="export-pdf-btn">
                <FilePdf size={18} className="mr-2 text-red-600" />{t('downloadPdf')}
              </Button>
              <Button onClick={handleExportExcel} variant="outline" className="rounded-none" data-testid="export-excel-btn">
                <FileXls size={18} className="mr-2 text-emerald-600" />{t('exportExcel')}
              </Button>
              <Button onClick={handleSendEmail} variant="outline" className="rounded-none" data-testid="send-email-btn">
                <PaperPlaneTilt size={18} className="mr-2 text-blue-600" />{t('sendViaEmail')}
              </Button>
              {/* Phase 707 — Send for Approval (DocuSign-style flow) */}
              <Button onClick={() => setApprovalOpen(true)} variant="outline" className="rounded-none" data-testid="send-for-approval-btn">
                <ShieldCheck size={18} className="mr-2 text-emerald-700" />Send for Approval
              </Button>
            </div>
          )}
        </div>

        {/* Phase 708b — Approval Status panel (badge + history + admin actions). */}
        {buildingId && year && (
          <ApprovalStatusPanel
            documentType="annual_statement"
            documentId={`${buildingId}_${year}`}
            documentLabel={`${t('annualFinancialStatement') || 'Annual Statement'} ${year}`}
            pdfUrl={`/api/buildings/${buildingId}/annual-statement/pdf?year=${year}&lang=${language}`}
            onSendNewVersion={() => setApprovalOpen(true)}
          />
        )}

        {/* Phase 707 — Send-for-Approval Dialog (generic component) */}
        <SendForApprovalDialog
          open={approvalOpen}
          onOpenChange={setApprovalOpen}
          documentType="annual_statement"
          documentId={`${buildingId}_${year}`}
          documentLabel={`${t('annualFinancialStatement') || 'Annual Statement'} ${year}`}
          pdfUrl={`/api/buildings/${buildingId}/annual-statement/pdf?year=${year}&lang=${language}`}
          pdfFilename={`Annual_Statement_${year}.pdf`}
          buildingId={buildingId}
          defaultOwnerEmail={data?.building?.committee_email || ''}
          defaultOwnerName={data?.building?.committee_name || ''}
        />

        {/* Statement Content */}
        {generated && data && (
          <div ref={printRef} className="space-y-6">
            {/* Header Card */}
            <div className="bg-white border border-zinc-200 p-6" data-testid="statement-header-card">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('annualFinancialStatement')}</h2>
                  <p className="text-sm"><span className="text-zinc-500">{t('buildingName')}:</span> <strong>{data.building.name}</strong></p>
                  <p className="text-sm"><span className="text-zinc-500">{t('address')}:</span> {data.building.location}</p>
                  <p className="text-sm"><span className="text-zinc-500">{t('period')}:</span> {data.period_start} - {data.period_end}</p>
                </div>
                <div className={`border-2 px-6 py-4 text-right ${data.summary.final_balance >= 0 ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`} data-testid="final-balance-box">
                  <p className="text-xs font-medium text-zinc-500 uppercase">{t('finalBalance')}</p>
                  <p className={`text-2xl font-bold font-mono ${data.summary.final_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    &euro;{data.summary.final_balance.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Phase 502 — Opening Balance summary block (per user request).
                Surfaces Opening Bank / Cash / Total in their own card at
                the very start of the statement so reconciliation against
                the bank book + cash book is immediate. The per-source
                opening rows still appear inside the transaction table
                below; this block is a separate snapshot, not a replacement. */}
            {(() => {
              // Phase 505 — Opening Balance block ALWAYS rendered for every
              // building. Even when both opening cash and bank balances are
              // €0.00 we still display the block (per global product spec:
              // no per-building configuration; users must always see the
              // same three rows so they can reconcile and decide if they
              // need to enter values). Numbers are loaded from the
              // building's own financial records (computed by the backend
              // via `_compute_source_state_as_of(prior_year_end)` plus the
              // in-year manual OB augmentation from Phase 503).
              const opCash = Number(data.summary?.opening_cash_balance ?? data.opening_cash_balance ?? 0);
              const opBank = Number(data.summary?.opening_bank_balance ?? data.opening_bank_balance ?? 0);
              const opTotal = Number(data.summary?.total_opening_balance ?? data.total_opening_balance ?? (opCash + opBank));
              return (
                <div className="bg-white border border-zinc-200 p-6" data-testid="opening-balance-block">
                  <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">{t('openingBalance')}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border border-zinc-200 px-4 py-3" data-testid="opening-bank-card">
                      <p className="text-[11px] text-zinc-500 uppercase">{t('openingBankBalance') || 'Opening Bank Balance'}</p>
                      <p className="text-lg font-bold font-mono text-zinc-800 mt-1">&euro;{opBank.toFixed(2)}</p>
                    </div>
                    <div className="border border-zinc-200 px-4 py-3" data-testid="opening-cash-card">
                      <p className="text-[11px] text-zinc-500 uppercase">{t('openingCashBalance') || 'Opening Cash Balance'}</p>
                      <p className="text-lg font-bold font-mono text-zinc-800 mt-1">&euro;{opCash.toFixed(2)}</p>
                    </div>
                    <div className="border-2 border-zinc-900 bg-zinc-50 px-4 py-3" data-testid="opening-total-card">
                      <p className="text-[11px] text-zinc-700 uppercase font-semibold">{t('totalOpeningBalance') || 'Total Opening Balance'}</p>
                      <p className="text-lg font-bold font-mono text-zinc-900 mt-1">&euro;{opTotal.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Transaction Table */}
            <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="statement-table-wrapper">
              {data.entries.length === 0 ? (
                <div className="p-12 text-center text-zinc-500">{t('noTransactions')}</div>
              ) : (
                <table className="excel-table" data-testid="annual-statement-table">
                  <thead>
                    <tr>
                      <th className="w-24">{t('date')}</th>
                      <th className="w-28">{t('reference')}</th>
                      <th>{t('description')}</th>
                      <th className="w-28 text-right">{t('income')} (&euro;)</th>
                      <th className="w-28 text-right">{t('expense')} (&euro;)</th>
                      <th className="w-28 text-right">{t('balance')} (&euro;)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map((entry, idx) => {
                      const isOB = !!entry.is_opening_balance;
                      const isCB = !!entry.is_closing_balance;
                      // Phase 330 — Visually separate closing rows from
                      // normal transactions: a thick top border on the
                      // first closing row + heavier weight + light bg.
                      const prevIsCB = idx > 0 && !!data.entries[idx - 1].is_closing_balance;
                      const isFirstClosing = isCB && !prevIsCB;
                      const isSupplierRefund = entry.source === 'supplier_refund';
                      const rowTint = isCB
                        ? 'bg-blue-50/60 font-semibold'
                        : isOB ? 'bg-blue-50/40'
                        : isSupplierRefund ? 'bg-emerald-50 ring-1 ring-inset ring-emerald-200'
                        : entry.income > 0 ? 'bg-emerald-50/30'
                        : entry.expense > 0 ? 'bg-red-50/20' : '';
                      const separatorClass = isFirstClosing ? 'border-t-2 border-t-zinc-900' : '';
                      return (
                      <tr key={idx} className={`${rowTint} ${separatorClass}`} data-testid={`statement-row-${idx}`}>
                        <td className="font-mono text-sm">{formatDateDMY(entry.date)}</td>
                        <td className="font-mono text-xs text-zinc-600" data-testid={`statement-ref-${idx}`}>{entry.reference || '—'}</td>
                        <td className="text-sm">
                          {/* Phase 328 — Opening AND closing balance rows: plain
                              description in blue, no category tag. */}
                          {(isOB || isCB) ? (
                            <span className="font-medium text-blue-700" data-testid={isOB ? `statement-opening-${idx}` : `statement-closing-${idx}`}>{entry.description}</span>
                          ) : entry.category ? (
                            <span>
                              <span className="inline-block px-1.5 py-0.5 mr-1.5 font-mono text-[10px] font-semibold bg-zinc-100 text-zinc-600 align-middle">
                                {getCatLabel(entry.category, t)}
                              </span>
                              <span>{entry.description}</span>
                            </span>
                          ) : (
                            entry.description
                          )}
                        </td>
                        {/* Phase 328 — Closing rows: blank Income/Expense
                            (the Balance column carries the per-source
                            closing snapshot). Opening rows show their
                            amount in the Income column like any other
                            ledger row. */}
                        <td className="font-mono text-right text-emerald-700 font-medium">
                          {isCB ? '' : (entry.income > 0 ? `€${entry.income.toFixed(2)}` : '')}
                        </td>
                        <td className="font-mono text-right text-red-700 font-medium">
                          {isCB ? '' : (entry.expense > 0 ? `€${entry.expense.toFixed(2)}` : '')}
                        </td>
                        <td className={`font-mono text-right font-semibold ${entry.balance >= 0 ? 'text-zinc-900' : 'text-red-700'}`}>
                          &euro;{entry.balance.toFixed(2)}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Summary */}
            <div className="bg-white border border-zinc-200 p-6" data-testid="statement-summary">
              <h3 className="text-sm font-semibold mb-4 uppercase tracking-wide text-zinc-500" style={{ fontFamily: 'var(--font-heading)' }}>{t('summary')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                  <span className="text-sm font-medium">{t('totalIncome')}</span>
                  <span className="font-mono text-emerald-700 font-bold text-lg">&euro;{data.summary.total_income.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-zinc-100">
                  <span className="text-sm font-medium">{t('totalExpenses')}</span>
                  <span className="font-mono text-red-700 font-bold text-lg">&euro;{data.summary.total_expenses.toFixed(2)}</span>
                </div>
                {/* Phase 517 — Surface supplier refund + credit totals as
                    their own lines so the user immediately sees the audit
                    breakdown. Both lines are hidden when zero so the
                    summary stays compact for buildings that don't use
                    supplier adjustments. */}
                {Number(data.summary.total_supplier_refunds || 0) > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-zinc-100" data-testid="annual-summary-supplier-refunds">
                    <span className="text-sm font-medium text-emerald-700">{t('totalSupplierRefunds') || 'Supplier Refunds'}</span>
                    <span className="font-mono text-emerald-700 font-bold text-lg">+&euro;{Number(data.summary.total_supplier_refunds).toFixed(2)}</span>
                  </div>
                )}
                {Number(data.summary.total_supplier_credits || 0) > 0 && (
                  <div className="flex justify-between items-center py-2 border-b border-zinc-100" data-testid="annual-summary-supplier-credits">
                    <span className="text-sm font-medium text-amber-700">{t('totalSupplierCredits') || 'Supplier Credits'}</span>
                    <span className="font-mono text-amber-700 font-bold text-lg">-&euro;{Number(data.summary.total_supplier_credits).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 bg-zinc-50 px-4 -mx-2">
                  <span className="font-semibold">{t('finalBalance')} (31/12/{year})</span>
                  <span className={`font-mono font-bold text-xl ${data.summary.final_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    &euro;{data.summary.final_balance.toFixed(2)}
                  </span>
                </div>
                {/* Phase 395 — Per-source closing balances (Cash / Bank / Total). */}
                {(data.summary.closing_cash_balance !== undefined || data.summary.closing_bank_balance !== undefined) && (
                  <div className="pt-3 mt-2 border-t border-zinc-200 space-y-2" data-testid="annual-closing-breakdown">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-600">{t('closingCashBalance') || 'Closing Cash Balance'}</span>
                      <span className="font-mono text-zinc-900" data-testid="closing-cash-balance">
                        &euro;{Number(data.summary.closing_cash_balance || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-600">{t('closingBankBalance') || 'Closing Bank Balance'}</span>
                      <span className="font-mono text-zinc-900" data-testid="closing-bank-balance">
                        &euro;{Number(data.summary.closing_bank_balance || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
                      <span className="font-semibold text-sm">{t('totalClosingBalance') || 'Total Closing Balance'}</span>
                      <span className="font-mono font-bold text-zinc-900" data-testid="total-closing-balance">
                        &euro;{Number(data.summary.total_closing_balance || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Category Breakdown */}
              {data.category_breakdown && Object.keys(data.category_breakdown).length > 0 && (
                <div className="mt-6 pt-4 border-t border-zinc-200">
                  <h4 className="text-xs font-semibold mb-3 uppercase tracking-wide text-zinc-400">{t('expenseBreakdown')}</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(data.category_breakdown).sort(([a], [b]) => a.localeCompare(b)).map(([cat, total]) => (
                      <div key={cat} className="flex justify-between text-sm bg-zinc-50 px-3 py-2 border border-zinc-100">
                        <span className="text-zinc-600 truncate mr-2">{cat === 'elevator_expense' ? t('elevator') : getCatLabel(cat, t)}</span>
                        <span className="font-mono font-medium">&euro;{total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Signature Section */}
            <div className="bg-white border border-zinc-200 p-6" data-testid="signature-section">
              <h3 className="text-sm font-semibold mb-8 uppercase tracking-wide text-zinc-500" style={{ fontFamily: 'var(--font-heading)' }}>{t('signatures')}</h3>
              <div className="grid grid-cols-3 gap-8 text-center">
                <div>
                  <div className="border-b-2 border-zinc-300 mb-2 h-16"></div>
                  <p className="text-sm font-medium">{t('president')}</p>
                </div>
                <div>
                  <div className="border-b-2 border-zinc-300 mb-2 h-16"></div>
                  <p className="text-sm font-medium">{t('secretary')}</p>
                </div>
                <div>
                  <div className="border-b-2 border-zinc-300 mb-2 h-16"></div>
                  <p className="text-sm font-medium">{t('treasurer')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!generated && !loading && (
          <div className="bg-white border border-zinc-200 p-16 text-center">
            <p className="text-zinc-400 text-sm">{t('selectYearAndGenerate')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
